// TipTap-based document editor (Phase 1).
// Provides inline text formatting (bold, italic, underline, etc.) via TipTap,
// while preserving the block-level data model for backend storage.

import { useEffect, useRef, useState } from 'react';
import { useEditor, EditorContent, type JSONContent } from '@tiptap/react';
import type { AnyExtension } from '@tiptap/core';
import { useI18n } from '@react/hooks';
import type { DocumentBlock, BlockType } from '@core/types/document';
import { reviewAnchorFromEditor, type DocumentReviewAnchor } from '@/features/document/review';
import { createExtensions } from './extensions';
import { FloatingToolbar } from './FloatingToolbar';
import {
  richTextToTipTapContent,
  tipTapContentToRichText,
  richTextToPlain,
} from './richtext-convert';

// ---------------------------------------------------------------------------
// Block ↔ TipTap document conversion
// ---------------------------------------------------------------------------

/** Map our BlockType to TipTap node type name */
function blockTypeToNodeType(type?: BlockType): string {
  switch (type) {
    case 'BLOCK_TYPE_HEADING_1':
      return 'heading';
    case 'BLOCK_TYPE_HEADING_2':
      return 'heading';
    case 'BLOCK_TYPE_HEADING_3':
      return 'heading';
    case 'BLOCK_TYPE_BULLETED_LIST_ITEM':
      return 'bulletList';
    case 'BLOCK_TYPE_NUMBERED_LIST_ITEM':
      return 'orderedList';
    case 'BLOCK_TYPE_TO_DO':
      return 'taskList';
    case 'BLOCK_TYPE_QUOTE':
      return 'blockquote';
    case 'BLOCK_TYPE_CODE':
      return 'codeBlock';
    case 'BLOCK_TYPE_DIVIDER':
      return 'horizontalRule';
    case 'BLOCK_TYPE_CALLOUT':
      return 'callout';
    case 'BLOCK_TYPE_TOGGLE':
      return 'toggle';
    case 'BLOCK_TYPE_CHART':
      return 'chart';
    case 'BLOCK_TYPE_DIAGRAM':
      return 'diagram';
    case 'BLOCK_TYPE_EQUATION':
      return 'equation';
    case 'BLOCK_TYPE_TABLE':
      return 'table';
    case 'BLOCK_TYPE_IMAGE':
      return 'image';
    default:
      return 'paragraph';
  }
}

/** Get heading level from BlockType */
function getHeadingLevel(type?: BlockType): number | undefined {
  switch (type) {
    case 'BLOCK_TYPE_HEADING_1':
      return 1;
    case 'BLOCK_TYPE_HEADING_2':
      return 2;
    case 'BLOCK_TYPE_HEADING_3':
      return 3;
    default:
      return undefined;
  }
}

/** Map TipTap node type back to our BlockType */
function nodeTypeToBlockType(nodeType: string, attrs?: Record<string, unknown>): BlockType {
  switch (nodeType) {
    case 'heading': {
      const level = attrs?.level as number;
      if (level === 1) return 'BLOCK_TYPE_HEADING_1';
      if (level === 2) return 'BLOCK_TYPE_HEADING_2';
      return 'BLOCK_TYPE_HEADING_3';
    }
    case 'bulletList':
      return 'BLOCK_TYPE_BULLETED_LIST_ITEM';
    case 'orderedList':
      return 'BLOCK_TYPE_NUMBERED_LIST_ITEM';
    case 'taskList':
      return 'BLOCK_TYPE_TO_DO';
    case 'blockquote':
      return 'BLOCK_TYPE_QUOTE';
    case 'codeBlock':
      return 'BLOCK_TYPE_CODE';
    case 'horizontalRule':
      return 'BLOCK_TYPE_DIVIDER';
    case 'callout':
      return 'BLOCK_TYPE_CALLOUT';
    case 'toggle':
      return 'BLOCK_TYPE_TOGGLE';
    case 'chart':
      return 'BLOCK_TYPE_CHART';
    case 'diagram':
      return 'BLOCK_TYPE_DIAGRAM';
    case 'equation':
      return 'BLOCK_TYPE_EQUATION';
    case 'table':
      return 'BLOCK_TYPE_TABLE';
    case 'image':
      return 'BLOCK_TYPE_IMAGE';
    default:
      return 'BLOCK_TYPE_PARAGRAPH';
  }
}

/** Convert a single DocumentBlock to a TipTap JSONContent node */
function blockToNode(block: DocumentBlock): JSONContent {
  const nodeType = blockTypeToNodeType(block.type);
  const content = richTextToTipTapContent(block.rich_text);
  const blockId = block.entity?.id;

  // Atom nodes (chart, diagram, equation) carry data as attributes
  if (nodeType === 'chart') {
    return {
      type: 'chart',
      attrs: {
        chartType: block.chart?.chart_type,
        dataSource: block.chart?.data_source,
        config: block.chart?.config,
        blockId: block.entity?.id,
      },
    };
  }
  if (nodeType === 'diagram') {
    return {
      type: 'diagram',
      attrs: {
        diagramType: block.diagram?.diagram_type,
        source: block.diagram?.source,
        renderedSvg: block.diagram?.rendered_svg,
        blockId: block.entity?.id,
      },
    };
  }
  if (nodeType === 'equation') {
    return {
      type: 'equation',
      attrs: {
        expression: block.equation?.expression || richTextToPlain(block.rich_text),
        blockId: block.entity?.id,
      },
    };
  }
  if (nodeType === 'horizontalRule') {
    return { type: 'horizontalRule', attrs: { blockId } };
  }
  if (nodeType === 'image') {
    return {
      type: 'image',
      attrs: { src: block.image?.url, alt: richTextToPlain(block.caption), blockId },
    };
  }

  // Heading
  if (nodeType === 'heading') {
    return {
      type: 'heading',
      attrs: { level: getHeadingLevel(block.type), blockId },
      content: content.length > 0 ? content : undefined,
    };
  }

  // List types: TipTap expects list > listItem > paragraph structure
  if (nodeType === 'bulletList') {
    return {
      type: 'bulletList',
      content: [
        {
          type: 'listItem',
          attrs: { blockId },
          content: [
            {
              type: 'paragraph',
              attrs: { blockId },
              content: content.length > 0 ? content : undefined,
            },
          ],
        },
      ],
    };
  }
  if (nodeType === 'orderedList') {
    return {
      type: 'orderedList',
      content: [
        {
          type: 'listItem',
          attrs: { blockId },
          content: [
            {
              type: 'paragraph',
              attrs: { blockId },
              content: content.length > 0 ? content : undefined,
            },
          ],
        },
      ],
    };
  }
  if (nodeType === 'taskList') {
    return {
      type: 'taskList',
      content: [
        {
          type: 'taskItem',
          attrs: { checked: block.to_do?.checked ?? false, blockId },
          content: [
            {
              type: 'paragraph',
              attrs: { blockId },
              content: content.length > 0 ? content : undefined,
            },
          ],
        },
      ],
    };
  }

  // Blockquote
  if (nodeType === 'blockquote') {
    return {
      type: 'blockquote',
      attrs: { blockId },
      content: [
        {
          type: 'paragraph',
          attrs: { blockId },
          content: content.length > 0 ? content : undefined,
        },
      ],
    };
  }

  // Code block
  if (nodeType === 'codeBlock') {
    return {
      type: 'codeBlock',
      attrs: { language: block.code?.language, blockId },
      content: content.length > 0 ? content : undefined,
    };
  }

  // Callout
  if (nodeType === 'callout') {
    return {
      type: 'callout',
      attrs: { icon: block.callout?.icon || '💡', blockId },
      content: content.length > 0 ? content : undefined,
    };
  }

  // Toggle
  if (nodeType === 'toggle') {
    return {
      type: 'toggle',
      attrs: { blockId },
      content: content.length > 0 ? content : undefined,
    };
  }

  // Default: paragraph
  return {
    type: 'paragraph',
    attrs: { blockId },
    content: content.length > 0 ? content : undefined,
  };
}

/** Convert an array of DocumentBlocks to a TipTap document JSONContent */
export function blocksToDocument(blocks: DocumentBlock[]): JSONContent {
  const content: JSONContent[] = [];
  for (const block of blocks) {
    if (block.is_deleted) continue;
    const node = blockToNode(block);
    const previous = content[content.length - 1];
    if (previous && ['bulletList', 'orderedList', 'taskList'].includes(node.type ?? '') && previous.type === node.type) {
      previous.content = [...(previous.content ?? []), ...(node.content ?? [])];
    } else {
      content.push(node);
    }
  }

  return {
    type: 'doc',
    content: content.length > 0 ? content : [{ type: 'paragraph' }],
  };
}

/** Extract inline content from a TipTap node, handling nested structures (list > listItem > paragraph) */
function extractInlineContent(node: JSONContent): JSONContent[] | undefined {
  // Direct text content
  if (node.content?.some((c) => c.type === 'text')) {
    return node.content;
  }
  // Nested: listItem > paragraph, blockquote > paragraph, etc.
  const firstChild = node.content?.[0];
  if (firstChild) {
    return extractInlineContent(firstChild);
  }
  return undefined;
}

function findBlockId(node: JSONContent): string | undefined {
  if (typeof node.attrs?.blockId === 'string' && node.attrs.blockId) return node.attrs.blockId;
  for (const child of node.content ?? []) {
    const id = findBlockId(child);
    if (id) return id;
  }
  return undefined;
}

/** Convert a TipTap document JSON back to DocumentBlock[] */
export function documentToBlocks(
  doc: JSONContent,
  existingBlocks: DocumentBlock[],
): DocumentBlock[] {
  // TipTap combines adjacent list items into one list container. The backend
  // stores one block per item, so each child must be restored separately.
  const nodes = (doc.content ?? []).flatMap((node) => {
    if (!['bulletList', 'orderedList', 'taskList'].includes(node.type ?? '')) return [node];
    return (node.content ?? []).map((item) => ({
      ...node,
      attrs: { ...node.attrs, blockId: findBlockId(item) },
      content: [item],
    }));
  });
  const result: DocumentBlock[] = [];
  const existingById = new Map(existingBlocks.map((block) => [block.entity?.id, block]));
  const usedIds = new Set<string>();

  for (let i = 0; i < nodes.length; i++) {
    const node = nodes[i];
    const blockType = nodeTypeToBlockType(node.type ?? 'paragraph', node.attrs);

    // Formatting can wrap, split, or merge nodes. Match by persistent identity,
    // never by array index, or text will be saved onto the wrong server block.
    const nodeId = findBlockId(node);
    const existing = nodeId && !usedIds.has(nodeId) ? existingById.get(nodeId) : undefined;
    if (existing && nodeId) usedIds.add(nodeId);
    const baseBlock: Partial<DocumentBlock> = existing
      ? { ...existing }
      : {
          entity: { id: nodeId || crypto.randomUUID(), type_id: '', type_name: '', platform_id: '', tenant_id: '', product_id: '' },
          document_id: existingBlocks[0]?.document_id ?? '',
          position: '',
          is_deleted: false,
          version: 0,
          rich_text: [],
          caption: [],
          has_children: false,
          children: [],
          comment_count: 0,
          has_unresolved_comments: false,
          created_by: '',
          last_edited_by: '',
        };

    baseBlock.type = blockType;

    // Extract text content
    const inlineContent = extractInlineContent(node);
    baseBlock.rich_text = tipTapContentToRichText(inlineContent);

    // Type-specific properties
    if (node.type === 'taskList') {
      const taskItem = node.content?.[0];
      baseBlock.to_do = { checked: taskItem?.attrs?.checked ?? false };
    }
    if (node.type === 'codeBlock') {
      baseBlock.code = { language: node.attrs?.language ?? '', wrap_lines: false };
    }
    if (node.type === 'callout') {
      baseBlock.callout = { icon: node.attrs?.icon ?? '💡' };
    }
    if (node.type === 'chart') {
      baseBlock.chart = {
        chart_type: node.attrs?.chartType,
        data_source: node.attrs?.dataSource,
        config: node.attrs?.config,
      };
    }
    if (node.type === 'diagram') {
      baseBlock.diagram = {
        diagram_type: node.attrs?.diagramType,
        source: node.attrs?.source,
        rendered_svg: node.attrs?.renderedSvg,
      };
    }
    if (node.type === 'equation') {
      baseBlock.equation = { expression: node.attrs?.expression };
    }
    if (node.type === 'image') {
      baseBlock.image = { url: node.attrs?.src };
    }

    result.push(baseBlock as DocumentBlock);
  }

  return result;
}

/** Adjacent inserted blocks can be persisted in one AppendBlocks request. */
export function groupNewBlockRuns(blocks: DocumentBlock[], knownIds: Set<string>): Array<{
  blocks: DocumentBlock[];
  afterBlockId?: string;
}> {
  const runs: Array<{ blocks: DocumentBlock[]; afterBlockId?: string }> = [];
  for (let i = 0; i < blocks.length; i++) {
    const id = blocks[i].entity?.id;
    if (!id || knownIds.has(id)) continue;
    const start = i;
    while (i + 1 < blocks.length) {
      const nextId = blocks[i + 1].entity?.id;
      if (!nextId || knownIds.has(nextId)) break;
      i++;
    }
    runs.push({ blocks: blocks.slice(start, i + 1), afterBlockId: blocks[start - 1]?.entity?.id });
  }
  return runs;
}

// ---------------------------------------------------------------------------
// DocumentEditor component
// ---------------------------------------------------------------------------

interface DocumentEditorProps {
  documentId: string;
  initialBlocks?: DocumentBlock[];
  onChange?: (blocks: DocumentBlock[]) => void;
  onBlockUpdate?: (block: DocumentBlock) => Promise<unknown>;
  onBlockCreate?: (block: DocumentBlock, afterBlockId?: string) => Promise<unknown>;
  onBlocksCreate?: (blocks: DocumentBlock[], afterBlockId?: string) => Promise<unknown>;
  onBlocksModify?: (updates: DocumentBlock[], deletes: string[]) => Promise<unknown>;
  onBlockDelete?: (blockId: string) => Promise<unknown>;
  readOnly?: boolean;
  /** Extra TipTap extensions (e.g. PageBreak) */
  extraExtensions?: AnyExtension[];
  /** Called when the TipTap editor instance is created/ready */
  onEditorReady?: (editor: import('@tiptap/react').Editor) => void;
  onReviewBlock?: (anchor: DocumentReviewAnchor) => void;
}

export function DocumentEditor({
  documentId,
  initialBlocks = [],
  onChange,
  onBlockUpdate,
  onBlockCreate,
  onBlocksCreate,
  onBlocksModify,
  onBlockDelete,
  readOnly = false,
  extraExtensions,
  onEditorReady,
  onReviewBlock,
}: DocumentEditorProps) {
  const { t } = useI18n();
  const blocksRef = useRef(initialBlocks);
  // Track block IDs that exist on the server (came from initialBlocks)
  const knownBlockIds = useRef<Set<string>>(
    new Set(initialBlocks.map((b) => b.entity?.id).filter(Boolean) as string[]),
  );
  const saveTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const retryAfterRef = useRef(0);
  const savingRef = useRef(false);
  const revisionRef = useRef(0);
  const savedRevisionRef = useRef(0);
  const loadedRef = useRef(false);
  const [saveError, setSaveError] = useState<'rate_limited' | 'other' | null>(null);
  const [hoveredBlock, setHoveredBlock] = useState<{ position: number; top: number; blockId?: string } | null>(null);

  const initialContent = blocksToDocument(
    initialBlocks.length > 0
      ? initialBlocks
      : [
          {
            entity: { id: crypto.randomUUID?.() ?? Math.random().toString(36).slice(2, 15), type_id: '', type_name: '', platform_id: '', tenant_id: '', product_id: '' },
            document_id: documentId,
            type: 'BLOCK_TYPE_PARAGRAPH' as BlockType,
            position: '',
            rich_text: [],
            caption: [],
            has_children: false,
            children: [],
            is_deleted: false,
            version: 0,
            comment_count: 0,
            has_unresolved_comments: false,
            created_by: '',
            last_edited_by: '',
          } as DocumentBlock,
        ],
  );

  /** Serialize a block to a stable string for comparison */
  const blockFingerprint = (b: DocumentBlock): string =>
    JSON.stringify({
      type: b.type,
      rt: b.rich_text,
      todo: b.to_do,
      callout: b.callout,
      code: b.code,
      heading: b.heading,
      chart: b.chart,
      diagram: b.diagram,
      equation: b.equation,
      image: b.image,
    });

  // Keep a fingerprint map of the last-saved state to avoid redundant updates
  const fingerprintRef = useRef<Map<string, string>>(new Map());

  // Populate initial fingerprints
  useEffect(() => {
    if (loadedRef.current && revisionRef.current !== savedRevisionRef.current) return;
    const fp = new Map<string, string>();
    for (const b of initialBlocks) {
      const id = b.entity?.id;
      if (id) fp.set(id, blockFingerprint(b));
    }
    fingerprintRef.current = fp;
    knownBlockIds.current = new Set(
      initialBlocks.map((b) => b.entity?.id).filter(Boolean) as string[],
    );
  }, [initialBlocks]);

  const flushSave = async () => {
    if (savingRef.current || revisionRef.current === savedRevisionRef.current) return;
    if (Date.now() < retryAfterRef.current) {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(flushSave, retryAfterRef.current - Date.now());
      return;
    }
    savingRef.current = true;
    const revision = revisionRef.current;
    const snapshot = blocksRef.current;
    let retryDelay = 300;
    try {
      const newIds = new Set(snapshot.map((b) => b.entity?.id).filter(Boolean));
      const updates = snapshot.filter((block) => {
        const id = block.entity?.id;
        return !!id && knownBlockIds.current.has(id) && blockFingerprint(block) !== fingerprintRef.current.get(id);
      });
      const deletes = [...knownBlockIds.current].filter((id) => !newIds.has(id));
      if (onBlocksModify && updates.length + deletes.length > 1) {
        await onBlocksModify(updates, deletes);
        for (const block of updates) fingerprintRef.current.set(block.entity!.id, blockFingerprint(block));
        for (const id of deletes) {
          knownBlockIds.current.delete(id);
          fingerprintRef.current.delete(id);
        }
      } else {
        for (const block of updates) {
          if (!onBlockUpdate) throw new Error('Block update is unavailable');
          await onBlockUpdate(block);
          fingerprintRef.current.set(block.entity!.id, blockFingerprint(block));
        }
        for (const id of deletes) {
          if (!onBlockDelete) throw new Error('Block deletion is unavailable');
          await onBlockDelete(id);
          knownBlockIds.current.delete(id);
          fingerprintRef.current.delete(id);
        }
      }
      for (const run of groupNewBlockRuns(snapshot, knownBlockIds.current)) {
        if (onBlocksCreate) {
          await onBlocksCreate(run.blocks, run.afterBlockId);
          for (const block of run.blocks) {
            const id = block.entity!.id;
            knownBlockIds.current.add(id);
            fingerprintRef.current.set(id, blockFingerprint(block));
          }
        } else {
          for (let i = 0; i < run.blocks.length; i++) {
            const block = run.blocks[i];
            if (!onBlockCreate) throw new Error('Block creation is unavailable');
            await onBlockCreate(block, i ? run.blocks[i - 1].entity?.id : run.afterBlockId);
            const id = block.entity!.id;
            knownBlockIds.current.add(id);
            fingerprintRef.current.set(id, blockFingerprint(block));
          }
        }
      }
      savedRevisionRef.current = revision;
      retryAfterRef.current = 0;
      setSaveError(null);
    } catch (error) {
      const rateLimited = (error as { status?: number })?.status === 429;
      retryDelay = rateLimited ? 60_000 : 3000;
      if (rateLimited) retryAfterRef.current = Date.now() + retryDelay;
      console.error('[DocumentEditor] Failed to save blocks:', (error as { status?: number })?.status ?? 'network error');
      setSaveError(rateLimited ? 'rate_limited' : 'other');
    } finally {
      savingRef.current = false;
      if (revisionRef.current !== savedRevisionRef.current) {
        if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
        saveTimerRef.current = setTimeout(flushSave, retryDelay);
      }
    }
  };

  const editor = useEditor({
    extensions: [
      ...createExtensions(),
      ...(extraExtensions ?? []),
    ],
    content: initialContent,
    editable: !readOnly,
    onUpdate: ({ editor: ed }: { editor: any }) => {
      const doc = ed.getJSON();
      const prevBlocks = blocksRef.current;
      const newBlocks = documentToBlocks(doc, prevBlocks);
      blocksRef.current = newBlocks;
      revisionRef.current++;
      onChange?.(newBlocks);

      // Debounced save with smart diff
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(flushSave, Math.max(400, retryAfterRef.current - Date.now()));
    },
  });

  // Notify parent when editor is ready
  useEffect(() => {
    if (editor) {
      onEditorReady?.(editor);
    }
  }, [editor, onEditorReady]);

  useEffect(() => {
    editor?.setEditable(!readOnly);
  }, [editor, readOnly]);

  // Sync when initialBlocks change externally
  useEffect(() => {
    if (editor && initialBlocks.length > 0 && !loadedRef.current && revisionRef.current === 0) {
      const currentJSON = JSON.stringify(editor.getJSON());
      const newDoc = blocksToDocument(initialBlocks);
      const newJSON = JSON.stringify(newDoc);
      if (currentJSON !== newJSON) {
        blocksRef.current = initialBlocks;
        editor.commands.setContent(newDoc, false);
      }
      loadedRef.current = true;
    }
  }, [initialBlocks, editor]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      if (revisionRef.current !== savedRevisionRef.current) void flushSave();
    };
  }, []);

  const updateHoveredBlock = (target: EventTarget | null) => {
    if (!editor || !(target instanceof Element)) return;
    const root = editor.view.dom;
    let element: Element | null = target;
    while (element && element.parentElement !== root) element = element.parentElement;
    if (!element || element === root) return;
    const editorRect = root.closest('.document-editor')?.getBoundingClientRect();
    if (!editorRect) return;
    const childIndex = Array.prototype.indexOf.call(root.children, element) as number;
    if (childIndex < 0) return;
    const position = editor.view.posAtDOM(root, childIndex);
    const node = editor.state.doc.nodeAt(position);
    if (!node) return;
    const top = Math.round(element.getBoundingClientRect().top - editorRect.top);
    const after = position + node.nodeSize;
    const nodeBlockId = typeof node.attrs.blockId === 'string' ? node.attrs.blockId : undefined;
    const blockId = target.closest('[data-block-id]')?.getAttribute('data-block-id') || nodeBlockId;
    setHoveredBlock((previous) => previous?.position === after && previous.top === top && previous.blockId === blockId
      ? previous : { position: after, top, blockId });
  };

  const insertBlockAfter = () => {
    if (!editor || !hoveredBlock) return;
    const position = hoveredBlock.position;
    editor.commands.insertContentAt(position, { type: 'paragraph', attrs: { blockId: crypto.randomUUID() } });
    editor.commands.focus(position + 1);
  };

  const reviewBlock = () => {
    if (!editor || !hoveredBlock?.blockId) return;
    const anchor = reviewAnchorFromEditor(editor, hoveredBlock.blockId);
    if (anchor) onReviewBlock?.(anchor);
  };

  return (
    <div className="document-editor relative min-h-[200px]" onMouseMove={(event) => updateHoveredBlock(event.target)} onMouseLeave={() => setHoveredBlock(null)}>
      {saveError && (
        <div role="alert" className="mb-4 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {saveError === 'rate_limited'
            ? 'Сервер временно ограничил частоту запросов. Повторим сохранение через минуту; пока не закрывайте страницу.'
            : 'Не удалось сохранить изменения. Текст остаётся в редакторе; повторяем сохранение.'}
          {saveError !== 'rate_limited' && <button type="button" className="ml-2 underline" onClick={() => void flushSave()}>Повторить сейчас</button>}
        </div>
      )}
      {editor && !readOnly && <FloatingToolbar editor={editor} />}
      {editor && !readOnly && hoveredBlock && (
        <button
          type="button"
          aria-label="Добавить блок"
          title="Добавить блок"
          onMouseDown={(event) => event.preventDefault()}
          onClick={insertBlockAfter}
          className="absolute -left-9 z-10 flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
          style={{ top: hoveredBlock.top }}
        >
          <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>
        </button>
      )}
      {editor && onReviewBlock && hoveredBlock?.blockId && (
        <button
          type="button"
          aria-label={t('document.review.comment')}
          title={t('document.review.comment')}
          onMouseDown={(event) => event.preventDefault()}
          onClick={reviewBlock}
          className="absolute -left-9 z-10 flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
          style={{ top: hoveredBlock.top + (readOnly ? 0 : 32) }}
        >
          <svg aria-hidden="true" width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z" /></svg>
        </button>
      )}
      <EditorContent
        editor={editor}
        className="prose max-w-none focus:outline-none
          [&_.ProseMirror]:outline-none [&_.ProseMirror]:min-h-[200px]
          [&_.ProseMirror]:text-[16px] [&_.ProseMirror]:leading-[1.65]
          [&_.ProseMirror_p]:my-1 [&_.ProseMirror_p]:text-foreground
          [&_.ProseMirror_h1]:text-3xl [&_.ProseMirror_h1]:font-bold [&_.ProseMirror_h1]:text-foreground [&_.ProseMirror_h1]:my-2
          [&_.ProseMirror_h2]:text-2xl [&_.ProseMirror_h2]:font-semibold [&_.ProseMirror_h2]:text-foreground [&_.ProseMirror_h2]:my-1.5
          [&_.ProseMirror_h3]:text-xl [&_.ProseMirror_h3]:font-semibold [&_.ProseMirror_h3]:text-foreground [&_.ProseMirror_h3]:my-1
          [&_.ProseMirror_blockquote]:border-l-[3px] [&_.ProseMirror_blockquote]:border-foreground [&_.ProseMirror_blockquote]:pl-4 [&_.ProseMirror_blockquote]:text-foreground
          [&_.ProseMirror_hr]:border-border [&_.ProseMirror_hr]:my-4
          [&_.ProseMirror_code]:bg-muted/70 [&_.ProseMirror_code]:rounded [&_.ProseMirror_code]:px-1 [&_.ProseMirror_code]:py-0.5 [&_.ProseMirror_code]:font-mono [&_.ProseMirror_code]:text-sm
          [&_.ProseMirror_pre]:bg-muted/70 [&_.ProseMirror_pre]:rounded-lg [&_.ProseMirror_pre]:p-4 [&_.ProseMirror_pre]:overflow-x-auto
          [&_.ProseMirror_ul]:list-disc [&_.ProseMirror_ul]:pl-6
          [&_.ProseMirror_ol]:list-decimal [&_.ProseMirror_ol]:pl-6
          [&_.ProseMirror_li]:my-0.5
          [&_.ProseMirror_table]:border-collapse [&_.ProseMirror_table]:w-full
          [&_.ProseMirror_td]:border [&_.ProseMirror_td]:border-border [&_.ProseMirror_td]:px-3 [&_.ProseMirror_td]:py-2 [&_.ProseMirror_td]:align-top
          [&_.ProseMirror_th]:border [&_.ProseMirror_th]:border-border [&_.ProseMirror_th]:px-3 [&_.ProseMirror_th]:py-2 [&_.ProseMirror_th]:bg-muted/50 [&_.ProseMirror_th]:font-medium
          [&_.ProseMirror_.is-empty::before]:content-[attr(data-placeholder)] [&_.ProseMirror_.is-empty::before]:text-muted-foreground/40 [&_.ProseMirror_.is-empty::before]:float-left [&_.ProseMirror_.is-empty::before]:h-0 [&_.ProseMirror_.is-empty::before]:pointer-events-none
          [&_.ProseMirror_img]:rounded-lg [&_.ProseMirror_img]:max-w-full"
      />
    </div>
  );
}
